/**
 * Notification Bell Component
 * 
 * Shows notification count and opens notification center
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Bell, Settings } from 'lucide-react';
import { NotificationCenter } from './NotificationCenter';
import { NotificationSettings } from './NotificationSettings';
import { useNotifications } from '../../hooks/useNotifications';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';

const BellContainer = styled.div`
  position: relative;
`;

const BellButton = styled.button<{ hasNotifications: boolean }>`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${Spacing.sm};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${ThemeColors.background.light};
  }
  
  svg {
    color: ${ThemeColors.text.primary};
    transition: color 0.2s ease;
  }
  
  &:hover svg {
    color: ${ThemeColors.primary.main};
  }
`;

const NotificationBadge = styled.div<{ count: number }>`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  background: ${ThemeColors.error.main};
  color: white;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: ${Typography.fontWeight.bold};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 2px solid white;
  
  ${props => props.count === 0 && `
    display: none;
  `}
  
  ${props => props.count > 99 && `
    min-width: 24px;
    font-size: 9px;
  `}
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const SettingsModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 1001;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

interface NotificationBellProps {
  className?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { isSupported, unreadCount } = useNotifications();

  // Use real notification count from the hook
  const notificationCount = unreadCount || 0;

  const handleBellClick = () => {
    setIsNotificationCenterOpen(true);
  };

  const handleCloseNotificationCenter = () => {
    setIsNotificationCenterOpen(false);
  };

  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
    setIsNotificationCenterOpen(false);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  return (
    <BellContainer className={className}>
      <BellButton 
        hasNotifications={notificationCount > 0}
        onClick={handleBellClick}
        title={`${notificationCount} unread notifications`}
      >
        <Bell size={20} />
        {notificationCount > 0 && (
          <NotificationBadge count={notificationCount}>
            {notificationCount > 99 ? '99+' : notificationCount}
          </NotificationBadge>
        )}
      </BellButton>

      {/* Notification Center */}
      <NotificationCenter
        isOpen={isNotificationCenterOpen}
        onClose={handleCloseNotificationCenter}
        onOpenSettings={handleOpenSettings}
      />

      {/* Settings Modal */}
      <ModalOverlay isOpen={isSettingsOpen} onClick={handleCloseSettings}>
        <SettingsModal isOpen={isSettingsOpen} onClick={(e) => e.stopPropagation()}>
          <NotificationSettings onClose={handleCloseSettings} />
        </SettingsModal>
      </ModalOverlay>
    </BellContainer>
  );
};
